import {useMemo} from "react";
import {useQueryClient} from "@tanstack/react-query";
import {useSnackbar} from "notistack";
import {useApiQuery} from "../api/hooks";
import apiClient from "../api/apiClient";
import {usePreloaderStore} from "../stores";

/**
 * Централизованный хук для управления состоянием страницы SmenaEvent.
 * Содержит данные о мероприятиях и методы для CRUD операций.
 */
export default function useSmenaEventsState(smenaId) {
    const queryClient = useQueryClient();
    const {enqueueSnackbar} = useSnackbar();
    const {show: showPreloader, hide: hidePreloader} = usePreloaderStore();

    // ============ Query Keys ============
    const queryKeys = {
        events: ['events', smenaId],
    };

    // ============ Queries ============
    const {data: eventsData, isLoading} = useApiQuery(
        queryKeys.events,
        `/api/camps/public/events?smenaId=${smenaId}`,
        {refetchOnMount: 'always', staleTime: 0, enabled: !!smenaId}
    );

    // ============ Computed Data ============
    const events = useMemo(() => {
        if (!eventsData) return [];
        return [...eventsData].sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
    }, [eventsData]);

    // Группировка по датам
    const groupedEvents = useMemo(() => {
        if (!events.length) return [];

        const grouped = events.reduce((acc, event) => {
            const date = new Date(event.startTime);
            const formattedDate = date.getFullYear() + "-" +
                String(date.getMonth() + 1).padStart(2, "0") + "-" +
                String(date.getDate()).padStart(2, "0");
            if (!acc.has(formattedDate)) {
                acc.set(formattedDate, []);
            }
            acc.get(formattedDate).push(event);
            return acc;
        }, new Map());

        return Array.from(grouped);
    }, [events]);

    // События для командного зачёта
    const competitionEvents = useMemo(() => {
        return events.filter(e => e.useInTeamCompetition);
    }, [events]);

    // ============ Invalidation ============
    const invalidateEvents = () => {
        queryClient.invalidateQueries({queryKey: queryKeys.events});
    };

    // ============ CRUD Actions ============
    const createEvent = async () => {
        showPreloader();
        try {
            await apiClient.post('/api/camps/admin/events/new', null, {
                params: {smenaId}
            });
            invalidateEvents();
            enqueueSnackbar("Мероприятие создано", {variant: "success"});
        } catch (error) {
            enqueueSnackbar("Ошибка при создании мероприятия", {variant: "error"});
            throw error;
        } finally {
            hidePreloader();
        }
    };

    const saveEvent = async (eventData, imageFile = null) => {
        showPreloader();
        try {
            const formData = new FormData();
            formData.append('event', new Blob([JSON.stringify({
                ...eventData,
                smenaId
            })], {type: 'application/json'}));

            if (imageFile) {
                formData.append('image', imageFile);
            }

            await apiClient.post('/api/camps/admin/events', formData);
            invalidateEvents();
            enqueueSnackbar("Мероприятие сохранено", {variant: "success"});
        } catch (error) {
            enqueueSnackbar("Ошибка при сохранении мероприятия", {variant: "error"});
            throw error;
        } finally {
            hidePreloader();
        }
    };

    const deleteEvent = async (eventId) => {
        showPreloader();
        try {
            await apiClient.delete(`/api/camps/admin/events/${eventId}`);
            invalidateEvents();
            enqueueSnackbar("Мероприятие удалено", {variant: "success"});
        } catch (error) {
            enqueueSnackbar("Ошибка при удалении мероприятия", {variant: "error"});
            throw error;
        } finally {
            hidePreloader();
        }
    };

    const deleteEventImage = async (eventId) => {
        showPreloader();
        try {
            await apiClient.delete(`/api/camps/admin/events/${eventId}/image`);
            invalidateEvents();
        } catch (error) {
            enqueueSnackbar("Ошибка при удалении картинки", {variant: "error"});
            throw error;
        } finally {
            hidePreloader();
        }
    };

    const updateCompetitionResult = async (competitionResultId, result) => {
        showPreloader();
        try {
            await apiClient.put(`/api/camps/protected/teams/competition/${competitionResultId}?result=${result}`);
            invalidateEvents();
        } catch (error) {
            enqueueSnackbar("Ошибка при обновлении результата", {variant: "error"});
            throw error;
        } finally {
            hidePreloader();
        }
    };

    return {
        // Data
        events,
        groupedEvents,
        competitionEvents,
        isLoading,

        // Actions
        createEvent,
        saveEvent,
        deleteEvent,
        deleteEventImage,
        updateCompetitionResult,

        // Invalidation
        invalidateEvents,
    };
}
